import React, { useState, useEffect, useContext } from "react";
import * as StyleDOM from "../../../Components/Applicant-Tracking/Candidates/CandidateDetails/CandidateDocuments.jsx";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DynamicContext } from "../../../Context/DynamicContext";
import { post, get } from "../../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  UPDATES_VENDORS_TRACK_FIELDS,
  VENDORS_TRACKING_LIST,
} from "../../../helper/url_helper";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import SelectInput from "../../../Common/Select-Input/SelectInput.js";
import TextArea from "../../../Common/TextArea/TextArea";
import Button from "../../../Common/Button/Button";
import AnimationPlayer from "../../../Common/Animation-Player/AnimationPlayer";
import NoHistoryAnimation from "../../../Assets/Json/No-History-Animation.json";
import OptionsData from "../../../Assets/Json/StaticOptions.json";
import {
  getStageStyles,
  getRecruiterName,
} from "../../../Common/Common-Function/CommonFunctions.js";
import { handleCustomTrim } from "../../../Common/Common-Function/CommonFunctions.js";
import VybogTALLoaderAnimation from "../../../Common/Dynamic-Animations/DynamicAnimations.js";
import VendorEmailHistory from "./VendorEmailHistory.js";
import "../Candidates/Candidates.css";

const VendorTrackingHistory = ({ selectedVendor, getVendorID }) => {
  const { isDarkMode, formatDateWithUserTimezone } = useContext(DynamicContext);
  const { StaticOptions = {} } = OptionsData || {};
  const { vendorStatus = [], vendorPriority = [] } = StaticOptions;
  const [vendorTracking, setVendorTracking] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendorStatusTouched, setVendorStatusTouched] = useState(false);
  const [vendorStatusError, setVendorStatusError] = useState("");

  const [vendorPriorityTouched, setVendorPriorityTouched] = useState(false);
  const [vendorPriorityError, setVendorPriorityError] = useState("");
  const [historyViewTab, setHistoryViewTab] = useState(0);
  const [hasEmailHistory, setHasEmailHistory] = useState(false);

  const editFormik = useFormik({
    initialValues: {
      editRemarks: "",
      editVendorStatus: "",
      editVendorPriority: "",
    },
    validationSchema: Yup.object({
      editRemarks: Yup.string().trim().required("Remarks is required"),
      editVendorStatus: Yup.string().required("Vendor Status is required"),
      editVendorPriority: Yup.string().required("Priority is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const trimmedValues = handleCustomTrim(values);
      if (
        trimmedValues.editVendorStatus &&
        trimmedValues.editVendorPriority &&
        trimmedValues.editRemarks
      ) {
        try {
          const updateVendorTracking = await post(
            VYLABS_BASE_URL,
            UPDATES_VENDORS_TRACK_FIELDS,
            {
              id: selectedVendor.id,
              priority: trimmedValues.editVendorPriority,
              status: trimmedValues.editVendorStatus,
              remarks: trimmedValues.editRemarks,
            },
          );
          if (updateVendorTracking?.id) {
            await fetchVendorTracking();
            resetForm();
            Toast("success", "Vendor tracking updated successfully");
          } else {
            await fetchVendorTracking();
            resetForm();
            Toast("success", "Vendor tracking updated successfully");
          }
        } catch (error) {
          console.error("Error updating vendor tracking:", error);
          Toast("error", "Failed to update vendor tracking");
        }
      } else {
        Toast("warning", "Please add all the tracking details");
      }
    },
  });

  const fetchVendorTracking = async () => {
    if (!selectedVendor?.id) {
      console.warn("No selected vendor ID, skipping fetchVendorTracking");
      return;
    }
    try {
      setIsInitialLoading(true);
      const vendorTrackingResponse = await get(
        VYLABS_BASE_URL,
        `${VENDORS_TRACKING_LIST}?id=${selectedVendor.id}`,
      );
      const trackingLogs = vendorTrackingResponse?.tracking_logs ?? [];
      const trackingNewValues = trackingLogs.map((log) => {
        let parsedNewValue = {};
        try {
          parsedNewValue =
            typeof log.new_value === "string"
              ? JSON.parse(log.new_value || "{}")
              : log.new_value || {};
        } catch (error) {
          console.error("Error parsing tracking log JSON:", error);
        }
        return {
          id: log.id,
          user_id: log.user_id,
          first_name: log.first_name,
          last_name: log.last_name,
          entity_type: log.entity_type,
          entity_id: log.entity_id,
          field_names: log.field_names,
          status: parsedNewValue.status || "No status available",
          priority: parsedNewValue.priority || "No priority available",
          remarks:
            typeof parsedNewValue.remarks === "string" &&
            parsedNewValue.remarks.trim() !== ""
              ? parsedNewValue.remarks
              : "N/A",
          old_value: log.old_value,
          new_value: log.new_value,
          created_at: log.created_at,
        };
      });
      setVendorTracking(trackingNewValues);
    } catch (err) {
      console.error("fetchVendorTracking error:", err);
      setVendorTracking([]);
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    if (selectedVendor?.id || getVendorID) {
      fetchVendorTracking();
    }
  }, [selectedVendor?.id, getVendorID]);

  useEffect(() => {
    if (vendorStatusTouched) {
      setVendorStatusError(
        editFormik.values.editVendorStatus ? "" : "Vendor Status is required",
      );
    }
  }, [editFormik.values.editVendorStatus, vendorStatusTouched]);

  useEffect(() => {
    if (vendorPriorityTouched) {
      setVendorPriorityError(
        editFormik.values.editVendorPriority ? "" : "Priority is required",
      );
    }
  }, [editFormik.values.editVendorPriority, vendorPriorityTouched]);

  const handleAddTrack = () => {
    setVendorStatusTouched(true);
    setVendorPriorityTouched(true);
    editFormik.handleSubmit();
  };
  const shouldShowHistoryHeading =
    historyViewTab === 0
      ? Array.isArray(vendorTracking) && vendorTracking.length > 0
      : hasEmailHistory;

  return (
    <div className="tracing-tab-container">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          marginBottom: "0px",
        }}
      >
        {shouldShowHistoryHeading ? (
          <StyleDOM.TrackingHistoryHeader style={{ margin: 0 }}>
            <h3
              style={{
                color: "var(--header__color)",
                fontSize: "16px",
                fontWeight: 700,
              }}
            >
              {historyViewTab === 0 ? "Track History" : "Email History"}
            </h3>
          </StyleDOM.TrackingHistoryHeader>
        ) : (
          <div />
        )}
        <div className="filter-stages" style={{ display: "inline-flex" }}>
          <Button
            label="Track History"
            iconPosition="left"
            iconStyle={{ marginRight: "8px" }}
            event={() => setHistoryViewTab(0)}
            className={`grid-btn left ${historyViewTab === 0 ? "active" : ""}`}
          />
          <Button
            label="Email History"
            iconPosition="left"
            iconStyle={{ marginRight: "8px" }}
            event={() => setHistoryViewTab(1)}
            className={`grid-btn right ${historyViewTab === 1 ? "active" : ""}`}
          />
        </div>
      </div>
      {historyViewTab === 1 ? (
        <VendorEmailHistory
          selectedVendor={selectedVendor}
          onHasEmailHistoryChange={setHasEmailHistory}
        />
      ) : (
        <>
          <div>
            <div className="client-tracking-maininnercontainer">
              <div className="upperoutercontainer">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0px",
                    gap: "48px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      height: "26px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      fontSize: "20px",
                      lineHeight: "26px",
                      letterSpacing: "-0.01em",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    Vendor Tracking
                  </div>
                </div>
                <form onSubmit={editFormik.handleSubmit}>
                  <div className="job-div">
                    <div className="textfield-container">
                      <span
                        className="detail-label"
                        style={{ marginBottom: "8px" }}
                      >
                        Remarks
                      </span>
                      <TextArea
                        placeholder="Enter Remarks"
                        className="ai-search-textfield"
                        name="editRemarks"
                        value={editFormik.values.editRemarks}
                        onChange={editFormik.handleChange}
                        onBlur={editFormik.handleBlur}
                        error={
                          editFormik.touched.editRemarks &&
                          editFormik.errors.editRemarks
                        }
                        errorMessage={editFormik.errors.editRemarks}
                      />
                    </div>
                    <div
                      className="job-auto"
                      style={{ display: "flex", gap: "20px" }}
                    >
                      <div
                        className="textfield-container"
                        style={{ width: "100%" }}
                      >
                        <span
                          className="detail-label"
                          style={{ marginBottom: "15px" }}
                        >
                          Priority
                        </span>
                        <SelectInput
                          placeholder="Select Priority"
                          options={vendorPriority}
                          name="editVendorPriority"
                          value={editFormik.values.editVendorPriority}
                          onChange={(e) => {
                            editFormik.handleChange(e);
                            setVendorPriorityTouched(false);
                            setVendorPriorityError("");
                          }}
                          onBlur={() => setVendorPriorityTouched(true)}
                          error={vendorPriorityTouched && vendorPriorityError}
                          errorMessage={editFormik.errors.editVendorPriority}
                          labelStyle={{ marginBottom: "10px" }}
                          className="detail-label"
                        />
                      </div>
                      <div
                        className="textfield-container"
                        style={{ width: "100%" }}
                      >
                        <span
                          className="detail-label"
                          style={{ width: "fit-content", marginBottom: "15px" }}
                        >
                          Vendor Status
                        </span>
                        <SelectInput
                          placeholder="Select Vendor Status"
                          options={vendorStatus}
                          name="editVendorStatus"
                          value={editFormik.values.editVendorStatus}
                          onChange={(e) => {
                            editFormik.handleChange(e);
                            setVendorStatusTouched(false);
                            setVendorStatusError("");
                          }}
                          onBlur={() => setVendorStatusTouched(true)}
                          error={vendorStatusTouched && vendorStatusError}
                          errorMessage={editFormik.errors.editVendorStatus}
                          labelStyle={{ marginBottom: "10px" }}
                          className="detail-label"
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        marginTop: "2%",
                        justifyContent: "flex-end",
                        gap: "10px",
                      }}
                    >
                      <Button
                        label="Clear"
                        isCancel={true}
                        event={() => editFormik.resetForm()}
                        style={{
                          height: "32px",
                          width: "66px",
                          borderRadius: "8px",
                          border: "1px solid #6860ee",
                        }}
                        disabled={isSubmitting}
                      />
                      <Button
                        label={isSubmitting ? "Submitting..." : "Add Track"}
                        type="submit"
                        disabled={isSubmitting}
                        onClick={handleAddTrack}
                        style={{
                          height: "32px",
                          width: "96px",
                          borderRadius: "8px",
                          fontFamily: "Satoshi",
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          textAlign: "center",
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <StyleDOM.ContentMainContainer className="contentMain-Container">
            {isInitialLoading ? (
              <VybogTALLoaderAnimation />
            ) : Array.isArray(vendorTracking) && vendorTracking.length > 0 ? (
              <>
                <StyleDOM.TrackingHistoryHeader>
                  Tracking History
                </StyleDOM.TrackingHistoryHeader>
                <StyleDOM.TrackingLowerDisplayContainer className="display-container">
                  {vendorTracking.map((tracking, index) => (
                    <StyleDOM.TrackingLowerContainer
                      className={
                        vendorTracking.length > 1
                          ? "tracking-lowercontainer"
                          : ""
                      }
                      key={tracking.id || index}
                    >
                      <StyleDOM.TrackingMainInnerContainer className="tracking-inner">
                        <StyleDOM.TrackingLowerInnerContainer className="lower-innercontainer">
                          <StyleDOM.TrackingContentDiv>
                            <StyleDOM.ContentDate className="content-date">
                              {formatDateWithUserTimezone(tracking.created_at)}
                            </StyleDOM.ContentDate>
                            <StyleDOM.ContentStatus
                              style={getStageStyles(tracking.status)}
                            >
                              {tracking.status || "No status available"}
                            </StyleDOM.ContentStatus>
                          </StyleDOM.TrackingContentDiv>
                        </StyleDOM.TrackingLowerInnerContainer>
                        <StyleDOM.ContentContainer className="contentcontainer">
                          <StyleDOM.ContentDescription className="contentdescription">
                            <StyleDOM.Content>
                              {tracking.remarks ||
                                "No remarks available for this tracking"}
                            </StyleDOM.Content>
                          </StyleDOM.ContentDescription>
                        </StyleDOM.ContentContainer>
                        <StyleDOM.RecruiterTag>
                          Recruiter: {getRecruiterName(tracking)}
                        </StyleDOM.RecruiterTag>
                      </StyleDOM.TrackingMainInnerContainer>
                    </StyleDOM.TrackingLowerContainer>
                  ))}
                </StyleDOM.TrackingLowerDisplayContainer>
              </>
            ) : (
              <StyleDOM.NoTrackingHistory className="no-tracking-history">
                <AnimationPlayer
                  animationSrc={NoHistoryAnimation}
                  playerHeight="200px"
                  playerWidth="300px"
                />
              </StyleDOM.NoTrackingHistory>
            )}
          </StyleDOM.ContentMainContainer>
        </>
      )}
    </div>
  );
};

export default VendorTrackingHistory;
